import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { logEvent } from '~/config/firebase.config';
import { ClientDatabase, IClient, Mode } from '~/types/client';
import { effectiveRegieView, REGIE_LIMITS, RegieState, RegieView } from '~/types/screen/regie';
import { useSyncClient } from '~/hooks/models/useClient';
import { useGames } from '~/hooks/models/useGame';
import { useTick } from '~/hooks/useTick';
import { useClientBranding } from '~/hooks/useClientBranding';
import { LoadingPage } from '~/pages/LoadingPage';
import { PublicLobbyView } from '~/components/dualScreen/PublicLobbyView';
import { useDualScreenAccess } from '~/components/dualScreen/useDualScreenAccess';
import { ProUpsell } from '~/components/dualScreen/ProUpsell';
import { DualScreenButton } from '~/components/dualScreen/DualScreenButton';
import {
    closePublicScreen,
    forgetDualScreen,
    openPublicScreen,
    publicWindowState,
    screenAccess,
} from '~/services/dualScreen/dualScreen';

import { CockpitContent } from '../AnimatorCockpit';
import styles from './RegiePage.module.css';

const TEXT_SAVE_MS = 600;
const FALLBACK_GAME = 'unblind-test';

/** Texte de la régie écrit dans Firestore après une courte pause de frappe (pas à chaque lettre). */
function useRegieText(client: IClient, key: 'eventName' | 'ticker' | 'message', remote: string) {
    const [value, setValue] = useState(remote);
    const timer = useRef<ReturnType<typeof setTimeout>>();
    const editing = useRef(false);
    const idRef = useRef(client.id);
    idRef.current = client.id;
    const valueRef = useRef(value);
    valueRef.current = value;
    // Page quittée juste après la frappe : la dernière saisie part quand même.
    useEffect(
        () => () => {
            clearTimeout(timer.current);
            if (editing.current) {
                ClientDatabase.setRegie(idRef.current, Mode.Anim, { [key]: valueRef.current }).catch(() => {});
            }
        },
        []
    );
    // Changé depuis l'autre régie (ordinateur ↔ téléphone) : on suit, sauf pendant la frappe.
    useEffect(() => {
        if (!editing.current) setValue(remote);
    }, [remote]);
    const change = (next: string) => {
        const v = next.slice(0, REGIE_LIMITS[key]);
        setValue(v);
        editing.current = true;
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            editing.current = false;
            ClientDatabase.setRegie(idRef.current, Mode.Anim, { [key]: v }).catch(() => {});
        }, TEXT_SAVE_MS);
    };
    /** Écrit tout de suite (avant d'afficher le message, par exemple). */
    const flush = () => {
        clearTimeout(timer.current);
        editing.current = false;
        return ClientDatabase.setRegie(idRef.current, Mode.Anim, { [key]: value });
    };
    return { value, change, flush };
}

export function RegieContent({ client }: { client: IClient }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const branding = useClientBranding(client.id);
    useTick(10_000);

    const screen = client.getScreen(Mode.Anim);
    const regie: RegieState | undefined = screen?.regie;
    const view: RegieView = effectiveRegieView(regie);
    const live = screen?.isLive() ?? false;
    const roster = screen?.roster ?? [];

    const eventName = useRegieText(client, 'eventName', regie?.eventName ?? '');
    const ticker = useRegieText(client, 'ticker', regie?.ticker ?? '');
    const message = useRegieText(client, 'message', regie?.message ?? '');

    // Tableau stable : un nouveau tableau à chaque rendu relancerait la lecture Firestore en boucle.
    const screenGameID = screen?.gameID;
    const gameIDs = useMemo(() => (screenGameID ? [screenGameID] : []), [screenGameID]);
    const games = useGames(gameIDs);
    const game = games?.find((g) => g.id === screen?.gameID);

    // L'écran public de cette page a été fermé (croix, Alt+F4) : on le dit.
    const [windowState, setWindowState] = useState(publicWindowState());
    useEffect(() => {
        const id = window.setInterval(() => setWindowState(publicWindowState()), 2000);
        return () => window.clearInterval(id);
    }, []);

    const [blocked, setBlocked] = useState(false);
    const reopen = async () => {
        logEvent('regie_reopen_public', client.id, { clientID: client.id });
        const res = await openPublicScreen(client.id, screen?.gameID || FALLBACK_GAME, await screenAccess());
        setBlocked(!res.opened);
        setWindowState(publicWindowState());
    };

    const show = async (next: RegieView) => {
        if (next === 'message') await message.flush().catch(() => {});
        logEvent('regie_view', client.id, { clientID: client.id, view: next });
        // Hors jeu, la partie attend : pas de manche qui file sous l'accueil ou le message.
        // Vue d'avant le message gardée dans la fiche : « Retirer » y revient, depuis
        // n'importe quelle régie (ordinateur, téléphone, page rechargée).
        const extra = next === 'message' && view !== 'message' ? { prevView: view } : {};
        ClientDatabase.setRegieView(client.id, Mode.Anim, next, extra).catch(() => {});
    };

    const quit = () => {
        logEvent('regie_quit', client.id, { clientID: client.id });
        // Cet ordinateur avait ouvert l'écran public : il redevient la TV (menu). Sinon
        // (régie sur téléphone, ou page rechargée), la télécommande : un téléphone sur
        // le menu TV entrerait lui-même dans le jeu suivant.
        const openedHere = publicWindowState() !== 'unknown';
        closePublicScreen();
        forgetDualScreen();
        ClientDatabase.setRegieView(client.id, Mode.Anim, 'game').catch(() => {});
        navigate(openedHere ? '/home' : `/animator/${client.id}`);
    };

    const views: { key: RegieView; label: string }[] = [
        { key: 'lobby', label: t('regie.view.lobby') },
        { key: 'game', label: t('regie.view.game') },
        { key: 'message', label: t('regie.view.message') },
    ];

    return (
        <div className={styles.page}>
            <header className={styles.top}>
                <span className={styles.brand}>
                    Unboared <b>{t('regie.title')}</b>
                </span>
                <span className={styles.venue}>{client.name}</span>
                <span className={classNames(styles.status, !live && styles.statusOff)}>
                    <span className={styles.dot} />
                    {live ? t('regie.status.live') : t('regie.status.offline')}
                </span>
                <span className={styles.topActions}>
                    {(!live || windowState === 'closed') && (
                        <button type="button" className={styles.btn} onClick={reopen}>
                            {t('regie.reopen')}
                        </button>
                    )}
                    <button type="button" className={styles.ghost} onClick={quit}>
                        {t('regie.quit')}
                    </button>
                </span>
            </header>
            {blocked && <p className={styles.alert}>{t('regie.blocked')}</p>}
            {windowState === 'closed' && <p className={styles.alert}>{t('regie.closed')}</p>}

            <div className={styles.grid}>
                <section className={styles.col} aria-labelledby="regie-screen">
                    <h2 id="regie-screen" className={styles.h}>
                        {t('regie.screen.title')}
                    </h2>
                    <div className={styles.seg} role="group" aria-label={t('regie.screen.title')}>
                        {views.map((v) => (
                            <button
                                key={v.key}
                                type="button"
                                className={classNames(view === v.key && styles.segOn)}
                                aria-pressed={view === v.key}
                                onClick={() => show(v.key)}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                    <div className={styles.mirror}>
                        <div className={styles.tv}>
                            {view === 'game' ? (
                                <div className={styles.gameShot}>
                                    {game?.cover && <img src={game.cover} alt="" />}
                                    <span>{game ? game.name : t('regie.screen.noGame')}</span>
                                </div>
                            ) : (
                                <PublicLobbyView
                                    preview
                                    eventName={eventName.value}
                                    venueName={client.name}
                                    brand={branding}
                                    link={screen?.gameID ? 'https://unboared.com' : null}
                                    players={roster}
                                    ticker={regie?.tickerOn ? ticker.value : ''}
                                    message={view === 'message' ? message.value || ' ' : null}
                                />
                            )}
                        </div>
                        <span className={styles.mirrorLabel}>{t('regie.screen.mirror')}</span>
                    </div>

                    <label className={styles.field}>
                        <span>{t('regie.fields.eventName')}</span>
                        <input
                            id="regie-event"
                            value={eventName.value}
                            placeholder={client.name}
                            maxLength={REGIE_LIMITS.eventName}
                            onChange={(e) => eventName.change(e.target.value)}
                        />
                    </label>
                    <div className={styles.field}>
                        <span className={styles.fieldHead}>
                            <label htmlFor="regie-ticker">{t('regie.fields.ticker')}</label>
                            <button
                                type="button"
                                className={classNames(styles.toggle, regie?.tickerOn && styles.toggleOn)}
                                aria-pressed={!!regie?.tickerOn}
                                aria-label={t('regie.fields.tickerToggle')}
                                onClick={() =>
                                    ClientDatabase.setRegie(client.id, Mode.Anim, {
                                        tickerOn: !regie?.tickerOn,
                                        ticker: ticker.value,
                                    }).catch(() => {})
                                }
                            />
                        </span>
                        <input
                            id="regie-ticker"
                            value={ticker.value}
                            placeholder={t('regie.fields.tickerPlaceholder')}
                            maxLength={REGIE_LIMITS.ticker}
                            onChange={(e) => ticker.change(e.target.value)}
                        />
                    </div>
                    <div className={styles.field}>
                        <label htmlFor="regie-message">{t('regie.fields.message')}</label>
                        <span className={styles.inline}>
                            <input
                                id="regie-message"
                                value={message.value}
                                placeholder={t('regie.fields.messagePlaceholder')}
                                maxLength={REGIE_LIMITS.message}
                                onChange={(e) => message.change(e.target.value)}
                            />
                            {view === 'message' ? (
                                <button
                                    type="button"
                                    className={classNames(styles.btn, styles.btnOn)}
                                    onClick={() => show(regie?.prevView ?? 'lobby')}
                                >
                                    {t('regie.fields.messageHide')}
                                </button>
                            ) : (
                                <button type="button" className={styles.btn} onClick={() => show('message')}>
                                    {t('regie.fields.messageShow')}
                                </button>
                            )}
                        </span>
                    </div>
                </section>

                <section className={classNames(styles.col, styles.center)} aria-label={t('regie.game.title')}>
                    <CockpitContent client={client} embedded />
                </section>

                <section className={styles.col} aria-labelledby="regie-room">
                    <h2 id="regie-room" className={styles.h}>
                        {t('regie.room.title')}
                        <span className={styles.muted}>{t('regie.room.count', { count: roster.length })}</span>
                    </h2>
                    {roster.length === 0 ? (
                        <p className={styles.muted}>{t('regie.room.empty')}</p>
                    ) : (
                        <ul className={styles.room}>
                            {roster.map((p) => (
                                <li key={p.id}>
                                    <span className={styles.avatar} style={{ background: p.color || undefined }}>
                                        {p.avatar ? <img src={p.avatar} alt="" /> : p.name.slice(0, 1).toUpperCase()}
                                    </span>
                                    <span className={styles.name}>{p.name}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
}

/**
 * Double écran « Régie » (formule Pro, offert pendant l'essai) : poste de pilotage
 * sur l'ordinateur de l'animateur. L'écran public (projecteur) affiche ce que la
 * régie choisit ; la télécommande habituelle est au centre.
 */
export function RegiePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { clientID } = useParams();
    const client = useSyncClient(clientID || '');
    const access = useDualScreenAccess(client);

    if (!client || access === 'loading') {
        return <LoadingPage>{t('animator.cockpit.connecting')}</LoadingPage>;
    }
    if (access === 'no') {
        return (
            <div className={`${styles.page} ${styles.upsellPage}`}>
                <ProUpsell clientID={client.id} onClose={() => navigate(`/animator/${client.id}`)} />
            </div>
        );
    }
    return <RegieContent client={client} />;
}

/** Développement local seulement (route absente du build de prod) : la régie sans connexion, sur la démo. */
export function RegieDevPage() {
    const { clientID } = useParams();
    const client = useSyncClient(clientID || 'demo');
    if (!client) return <LoadingPage>…</LoadingPage>;
    return <RegieContent client={client} />;
}

/** Développement local seulement : le bouton « Double écran » du menu, sans connexion, sur la démo. */
export function DualScreenDevPage() {
    const { clientID } = useParams();
    const client = useSyncClient(clientID || 'demo');
    if (!client) return <LoadingPage>…</LoadingPage>;
    return (
        <div className={styles.page} style={{ padding: 40 }}>
            <DualScreenButton client={client} gameID="unblind-test" forceAccess />
        </div>
    );
}
